import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Calendar, 
  Calculator, 
  RotateCcw, 
  Trash2, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminTab = ({ leagueId, token, isAdmin, onDataChange }) => {
  const [matchdays, setMatchdays] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [isLoadingMatchdays, setIsLoadingMatchdays] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [isCreatingMatchday, setIsCreatingMatchday] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResettingStandings, setIsResettingStandings] = useState(false);
  const [isResettingChampionship, setIsResettingChampionship] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  
  const [selectedMatchdayToCalculate, setSelectedMatchdayToCalculate] = useState('');
  const [selectedMatchdayToReset, setSelectedMatchdayToReset] = useState('');
  const [showResetStandingsDialog, setShowResetStandingsDialog] = useState(false);
  const [showResetChampionshipDialog, setShowResetChampionshipDialog] = useState(false);
  const [showDeleteEventDialog, setShowDeleteEventDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const fetchMatchdays = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/matchdays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMatchdays(data);
      }
    } catch (err) {
      console.error('Error fetching matchdays:', err);
    } finally {
      setIsLoadingMatchdays(false);
    }
  }, [leagueId, token]);

  const fetchRecentEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/events/recent?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRecentEvents(data);
      }
    } catch (err) {
      console.error('Error fetching recent events:', err);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [leagueId, token]);

  useEffect(() => {
    if (isAdmin) {
      fetchMatchdays();
      fetchRecentEvents();
    }
  }, [isAdmin, fetchMatchdays, fetchRecentEvents]);

  const handleCreateMatchday = async () => {
    setIsCreatingMatchday(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/matchdays/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Nuova giornata creata!');
        await fetchMatchdays();
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella creazione della giornata');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsCreatingMatchday(false);
    }
  };

  const handleCalculateMatchday = async () => {
    if (!selectedMatchdayToCalculate) {
      toast.error('Seleziona una giornata da calcolare');
      return;
    }

    setIsCalculating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/leagues/${leagueId}/matchdays/${selectedMatchdayToCalculate}/calculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Giornata calcolata con successo!');
        await fetchMatchdays();
        setSelectedMatchdayToCalculate('');
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel calcolo della giornata');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleResetMatchday = async () => {
    if (!selectedMatchdayToReset) {
      toast.error('Seleziona una giornata da annullare');
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/matchdays/${selectedMatchdayToReset}/reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Giornata annullata! Punti campionato e fantasy rimossi.');
        await fetchMatchdays();
        await fetchRecentEvents();
        setSelectedMatchdayToReset('');
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'annullamento della giornata');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetStandings = async () => {
    setIsResettingStandings(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/reset-standings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Lega resettata completamente!');
        await fetchMatchdays();
        await fetchRecentEvents();
        setShowResetStandingsDialog(false);
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel reset della lega');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsResettingStandings(false);
    }
  };

  const handleResetChampionship = async () => {
    setIsResettingChampionship(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/reset-championship-only`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Campionato resettato! Fantasy points mantenuti.');
        await fetchMatchdays();
        setShowResetChampionshipDialog(false);
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel reset del campionato');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsResettingChampionship(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    setIsDeletingEvent(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Evento eliminato con successo!');
        await fetchRecentEvents();
        setShowDeleteEventDialog(false);
        setEventToDelete(null);
        onDataChange?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'eliminazione dell\'evento');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsDeletingEvent(false);
    }
  };

  const getEventIcon = (points) => {
    if (points > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (points < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };

  const getEventColor = (points) => {
    if (points > 0) return 'border-green-200 bg-green-50';
    if (points < 0) return 'border-red-200 bg-red-50';
    return 'border-gray-200 bg-gray-50';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
        <p className="text-lg font-semibold">Accesso Negato</p>
        <p className="text-sm text-muted-foreground mt-2">
          Solo gli admin possono accedere a questa sezione
        </p>
      </div>
    );
  }

  const pendingMatchdays = matchdays.filter(m => m.status === 'pending');
  const calculatedMatchdays = matchdays.filter(m => m.status === 'calculated');

  return (
    <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Gestione Giornate */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            Gestione Giornate
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Crea, calcola o annulla giornate di campionato
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
          {/* Crea Giornata */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-900/20">
            <div className="flex-1">
              <p className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-gray-100">Crea Nuova Giornata</p>
              <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                Genera automaticamente i match per la prossima giornata
              </p>
            </div>
            <Button
              onClick={handleCreateMatchday}
              disabled={isCreatingMatchday}
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 w-full sm:w-auto text-sm whitespace-nowrap"
              size="sm"
            >
              {isCreatingMatchday ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Crea
                </>
              )}
            </Button>
          </div>

          {/* Calcola Giornata */}
          <div className="space-y-2 p-3 sm:p-4 border rounded-lg bg-green-50/50 dark:bg-green-900/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex-1">
                <p className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-gray-100">Calcola Giornata</p>
                <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                  Finalizza i risultati e assegna i punti campionato
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <Select
                  value={selectedMatchdayToCalculate}
                  onValueChange={setSelectedMatchdayToCalculate}
                >
                  <SelectTrigger className="text-sm w-full">
                    <SelectValue placeholder="Seleziona giornata da calcolare" />
                  </SelectTrigger>
                  <SelectContent>
                    {pendingMatchdays.length === 0 ? (
                      <div className="p-2 text-xs sm:text-sm text-muted-foreground text-center">
                        Nessuna giornata pendente
                      </div>
                    ) : (
                      pendingMatchdays.map((md) => (
                        <SelectItem key={md.id} value={md.id} className="text-sm">
                          Giornata {md.matchday_number} (In Corso)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleCalculateMatchday}
                disabled={isCalculating || !selectedMatchdayToCalculate}
                className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 w-full sm:w-auto text-sm whitespace-nowrap flex-shrink-0"
                size="sm"
              >
                {isCalculating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Calculator className="h-4 w-4 mr-2" />
                    Calcola
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Annulla Giornata */}
          <div className="space-y-2 p-3 sm:p-4 border rounded-lg bg-orange-50/50 dark:bg-orange-900/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex-1">
                <p className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-gray-100">Annulla Giornata</p>
                <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                  Rimuove punti campionato e fantasy della giornata
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <Select
                  value={selectedMatchdayToReset}
                  onValueChange={setSelectedMatchdayToReset}
                >
                  <SelectTrigger className="text-sm w-full">
                    <SelectValue placeholder="Seleziona giornata da annullare" />
                  </SelectTrigger>
                  <SelectContent>
                    {calculatedMatchdays.length === 0 ? (
                      <div className="p-2 text-xs sm:text-sm text-muted-foreground text-center">
                        Nessuna giornata calcolata
                      </div>
                    ) : (
                      calculatedMatchdays.map((md) => (
                        <SelectItem key={md.id} value={md.id} className="text-sm">
                          Giornata {md.matchday_number} (Completata)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleResetMatchday}
                disabled={isResetting || !selectedMatchdayToReset}
                variant="destructive"
                className="bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800 w-full sm:w-auto text-sm whitespace-nowrap flex-shrink-0"
                size="sm"
              >
                {isResetting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Annulla
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reset Lega */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            Reset Lega
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Resetta il campionato o l'intera lega
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-4 sm:p-6">
          {/* Reset Solo Campionato */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 border-2 border-yellow-200 dark:border-yellow-700 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
            <div className="flex-1">
              <p className="font-semibold text-xs sm:text-sm text-yellow-900 dark:text-yellow-100">Reset Solo Campionato</p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Resetta giornate e punti campionato, ma mantiene fantasy points ed eventi
              </p>
            </div>
            <Button
              onClick={() => setShowResetChampionshipDialog(true)}
              variant="outline"
              className="border-yellow-600 dark:border-yellow-500 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 w-full sm:w-auto text-sm"
              size="sm"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Campionato
            </Button>
          </div>
          
          {/* Reset Completo */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 border-2 border-red-200 dark:border-red-700 rounded-lg bg-red-50 dark:bg-red-900/20">
            <div className="flex-1">
              <p className="font-semibold text-xs sm:text-sm text-red-900 dark:text-red-100">Reset Completo</p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                ⚠️ Elimina TUTTO: punti, giornate, eventi. Non può essere annullato!
              </p>
            </div>
            <Button
              onClick={() => setShowResetStandingsDialog(true)}
              variant="destructive"
              className="w-full sm:w-auto text-sm whitespace-nowrap"
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Reset Totale
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gestione Eventi Recenti */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            Gestione Eventi
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Ultimi 30 bonus/malus assegnati - Elimina eventi sbagliati
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {isLoadingEvents ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : recentEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nessun evento presente
            </div>
          ) : (
            <ScrollArea className="h-[300px] sm:h-[400px] pr-2">
              <div className="space-y-2 pr-2">
                {recentEvents.map((event) => (
                  <Card
                    key={event.id}
                    className={`border-l-4 ${getEventColor(event.points)} dark:bg-gray-800 dark:border-l-4`}
                  >
                    <CardContent className="p-2 sm:p-3">
                      <div className="flex items-start justify-between gap-2 sm:gap-3">
                        <div className="flex items-start gap-1 sm:gap-2 flex-1 min-w-0">
                          {getEventIcon(event.points)}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-xs sm:text-sm truncate text-gray-900 dark:text-gray-100">
                              {event.professor_name}
                              <span className="text-muted-foreground dark:text-gray-400 font-normal ml-1 sm:ml-2 text-xs">
                                ({event.professor_subject})
                              </span>
                            </div>
                            {event.team_name && (
                              <div className="text-xs text-muted-foreground dark:text-gray-400 mb-1">
                                Team: {event.team_name}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                              {event.description}
                            </div>
                            <div className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                              {formatDate(event.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                          <div
                            className={`text-base sm:text-lg font-bold whitespace-nowrap ${
                              event.points > 0
                                ? 'text-green-600 dark:text-green-400'
                                : event.points < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {event.points > 0 ? '+' : ''}
                            {event.points}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEventToDelete(event);
                              setShowDeleteEventDialog(true);
                            }}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <X className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Dialog Conferma Reset Solo Campionato */}
      <Dialog open={showResetChampionshipDialog} onOpenChange={setShowResetChampionshipDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-700 text-base sm:text-lg">
              <RotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />
              Conferma Reset Campionato
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Questa azione resetterà:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3 sm:py-4">
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 bg-yellow-100 text-yellow-800 text-xs">✓</Badge>
              <p className="text-xs sm:text-sm">Tutti i punti campionato (V/P/S) azzerati</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 bg-yellow-100 text-yellow-800 text-xs">✓</Badge>
              <p className="text-xs sm:text-sm">Tutte le giornate cancellate</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 bg-green-100 text-green-800 text-xs">✓</Badge>
              <p className="text-xs sm:text-sm font-semibold">I fantasy points dei professori SARANNO MANTENUTI</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 bg-green-100 text-green-800 text-xs">✓</Badge>
              <p className="text-xs sm:text-sm font-semibold">Gli eventi bonus/malus SARANNO MANTENUTI</p>
            </div>
            <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs sm:text-sm font-semibold text-blue-900">
                💡 Usa questa opzione per riavviare il campionato mantenendo i punti fantasy
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetChampionshipDialog(false)}
              disabled={isResettingChampionship}
              className="w-full sm:w-auto text-sm"
              size="sm"
            >
              Annulla
            </Button>
            <Button
              onClick={handleResetChampionship}
              disabled={isResettingChampionship}
              className="bg-yellow-600 hover:bg-yellow-700 w-full sm:w-auto text-sm"
              size="sm"
            >
              {isResettingChampionship ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Conferma Reset Campionato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Reset Completo */}
      <Dialog open={showResetStandingsDialog} onOpenChange={setShowResetStandingsDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 text-base sm:text-lg">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
              Conferma Reset Completo
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Questa azione eliminerà PERMANENTEMENTE:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3 sm:py-4">
            <div className="flex items-start gap-2">
              <Badge variant="destructive" className="mt-0.5 text-xs">⚠️</Badge>
              <p className="text-xs sm:text-sm">Tutti i punti campionato di tutti i team</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge variant="destructive" className="mt-0.5 text-xs">⚠️</Badge>
              <p className="text-xs sm:text-sm">Tutti i punti fantasy di tutti i professori</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge variant="destructive" className="mt-0.5 text-xs">⚠️</Badge>
              <p className="text-xs sm:text-sm">Tutte le giornate create</p>
            </div>
            <div className="flex items-start gap-2">
              <Badge variant="destructive" className="mt-0.5 text-xs">⚠️</Badge>
              <p className="text-xs sm:text-sm">Tutti gli eventi bonus/malus</p>
            </div>
            <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs sm:text-sm font-semibold text-yellow-900">
                ⚠️ Questa operazione NON può essere annullata!
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetStandingsDialog(false)}
              disabled={isResettingStandings}
              className="w-full sm:w-auto text-sm"
              size="sm"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetStandings}
              disabled={isResettingStandings}
              className="w-full sm:w-auto text-sm"
              size="sm"
            >
              {isResettingStandings ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Conferma Reset Completo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione Evento */}
      <Dialog open={showDeleteEventDialog} onOpenChange={setShowDeleteEventDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Elimina Evento</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Sei sicuro di voler eliminare questo evento?
            </DialogDescription>
          </DialogHeader>
          {eventToDelete && (
            <div className="py-3 sm:py-4">
              <Card className={`border-l-4 ${getEventColor(eventToDelete.points)}`}>
                <CardContent className="p-2 sm:p-3">
                  <div className="font-semibold text-xs sm:text-sm">
                    {eventToDelete.professor_name} ({eventToDelete.professor_subject})
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {eventToDelete.team_name && `Team: ${eventToDelete.team_name}`}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {eventToDelete.description}
                  </div>
                  <div className="text-base sm:text-lg font-bold mt-2">
                    Punti: {eventToDelete.points > 0 ? '+' : ''}
                    {eventToDelete.points}
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs sm:text-sm text-muted-foreground mt-3">
                I punti verranno rimossi dal professore e dal team associato.
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteEventDialog(false);
                setEventToDelete(null);
              }}
              disabled={isDeletingEvent}
              className="w-full sm:w-auto text-sm"
              size="sm"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEvent}
              disabled={isDeletingEvent}
              className="w-full sm:w-auto text-sm"
              size="sm"
            >
              {isDeletingEvent ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
